import { useMemo } from "react";
import cn from "classnames";
import { ArrowDown2, ArrowUp2, Add } from "iconsax-react";
import { Avatar, Badge, Table } from "@/shared/ui";
import { formatDateForDisplay } from "@/shared/utils";
import type { TaskListItem, TaskStatus } from "../../../types";
import { TASK_STATUS_MAP, PRIORITY_MAP, COLUMN_HEADER_STYLES } from "../../../consts";

const NEAR_DEADLINE_DAYS = 3;
const COLUMN_COUNT = 5;

function getDeadlineStats(tasks: TaskListItem[]): { nearDeadline: number; overdue: number } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let nearDeadline = 0;
  let overdue = 0;
  for (const task of tasks) {
    if (!task.due_date) continue;
    const due = new Date(task.due_date);
    due.setHours(0, 0, 0, 0);
    const diffDays = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) {
      overdue++;
    } else if (diffDays <= NEAR_DEADLINE_DAYS) {
      nearDeadline++;
    }
  }
  return { nearDeadline, overdue };
}

interface StatusGroupProps {
  status: TaskStatus;
  tasks: TaskListItem[];
  isCollapsed: boolean;
  styles: (typeof COLUMN_HEADER_STYLES)[TaskStatus];
  projectId: number;
  onToggle: () => void;
  onTaskClick: (task: TaskListItem, projectId: number) => void;
  onCreateTask?: (status: TaskStatus, projectId: number) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
  tTasks: (key: string) => string;
}

export default function StatusGroup({
  status,
  tasks,
  isCollapsed,
  styles,
  projectId,
  onToggle,
  onTaskClick,
  onCreateTask,
  t,
  tTasks,
}: StatusGroupProps) {
  const deadlineStats = useMemo(() => getDeadlineStats(tasks), [tasks]);

  return (
    <>
      <tr className="cursor-pointer select-none surface-base-fill border-b surface-container-stroke" onClick={onToggle}>
        <td colSpan={COLUMN_COUNT} className="pt-4 pb-3 px-2">
          <div className="flex justify-between items-center gap-2">
            <div className="flex justify-start items-center gap-2">
              <span className="content-base-secondary">
                {isCollapsed ? (
                  <ArrowDown2 size={16} color="currentColor" />
                ) : (
                  <ArrowUp2 size={16} color="currentColor" />
                )}
              </span>
              <p className="text-body-bold-md content-base-primary">{tTasks(TASK_STATUS_MAP[status].label)}</p>
              <span
                className={cn("px-1.5 h-5 radius-2xs flex justify-center items-center text-label-xs", styles.badge)}>
                {t("list.taskCount", { count: tasks.length })}
              </span>
            </div>
            <div className="flex justify-end items-center pr-2 gap-2">
              <p className="text-body-bold-xs content-base-secondary">
                {t("list.nearDeadline", { count: deadlineStats.nearDeadline })}
              </p>
              <p className="text-body-bold-xs content-base-secondary">•</p>
              <p className="text-body-bold-xs content-base-secondary">
                {t("list.overdue", { count: deadlineStats.overdue })}
              </p>
            </div>
          </div>
        </td>
      </tr>
      {!isCollapsed && (
        <>
          {tasks.map((task) => {
            const priorityInfo = PRIORITY_MAP[task.priority];
            return (
              <Table.Row
                key={task.id}
                onClick={() => onTaskClick(task, projectId)}
                className="cursor-pointer surface-light-stroke">
                <Table.Cell className="text-label-xs content-base-primary">{task.id}</Table.Cell>
                <Table.Cell className="text-label-xs content-base-primary">{task.title}</Table.Cell>
                <Table.Cell>
                  {task.assigned_to_name ? (
                    <div className="flex items-center gap-2">
                      <Avatar name={task.assigned_to_name} size={24} textClassName="text-[10px]" />
                      <span className="text-label-xs content-base-primary truncate">{task.assigned_to_name}</span>
                    </div>
                  ) : (
                    <span className="text-label-xs content-base-primary">{t("list.unassigned")}</span>
                  )}
                </Table.Cell>
                <Table.Cell className="text-body-regular-sm content-base-secondary">
                  {task.due_date ? formatDateForDisplay(task.due_date) : t("list.noDueDate")}
                </Table.Cell>
                <Table.Cell>
                  <Badge variant="soft" color={priorityInfo.color} text={tTasks(priorityInfo.label)} dot />
                </Table.Cell>
              </Table.Row>
            );
          })}
          {onCreateTask && (
            <tr
              className="border-b surface-light-stroke last:border-none cursor-pointer"
              onClick={() => onCreateTask(status, projectId)}>
              <td colSpan={COLUMN_COUNT} className="py-4 px-3">
                <div className="flex justify-start items-center gap-1 content-base-low">
                  <Add size={12} color="currentColor" />
                  <p className="text-label-xs content-base-low">{t("list.createTask")}</p>
                </div>
              </td>
            </tr>
          )}
        </>
      )}
    </>
  );
}
